/**
 * Synthetic form-only keys that live in the RHF form state but must NEVER be persisted
 * to the backend save payload.
 *
 * Conventions:
 * - All keys start with `_` so `handleSave` can strip them with a single prefix check.
 * - Use the exported constants (not raw strings) everywhere — single source of truth
 *   prevents typos silently leaking to the save payload.
 */

/** Gate flag: true once master-password-encrypted fields have been successfully decrypted. */
export const SYNTHETIC_MASTER_PASSWORD_UNLOCKED = '_masterPasswordUnlocked';

/**
 * Preview-blocked flag: true when a live-preview target URL cannot be embedded in an iframe
 * (X-Frame-Options / CSP `frame-ancestors`), set by the `verifyEmbeddableUrl` action handler when
 * run via `runOnChange`. A manifest `notice` field reads it via
 * `displayOptions.showIf: { _previewBlocked: [true] }` to surface a "preview not available" message
 * (Website app — mirrors the V2 remote form's header-checker behaviour).
 */
export const SYNTHETIC_PREVIEW_BLOCKED = '_previewBlocked';

/** All synthetic keys (for validation / filtering). */
export const SYNTHETIC_FORM_KEYS = new Set<string>([
  SYNTHETIC_MASTER_PASSWORD_UNLOCKED,
  SYNTHETIC_PREVIEW_BLOCKED,
]);

/** Prefix used by synthetic keys. Used by `handleSave` to strip them from the payload. */
export const SYNTHETIC_KEY_PREFIX = '_';

/** True if `key` is a synthetic form-only key (named flag OR `_`-prefixed convention). */
export function isReservedFormKey(key: string): boolean {
  return SYNTHETIC_FORM_KEYS.has(key) || key.startsWith(SYNTHETIC_KEY_PREFIX);
}
